import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring, useScroll } from 'framer-motion';
import './PointerAccent.css';
import { useMotionPreference } from '../hooks/useMotionPreference';
import { useSpaceExperience } from './SpaceExperience';

// A subtle orbit around the native cursor. The click web lives on the canvas.
export default function PointerAccent() {
  const systemReduced = useMotionPreference();
  const space = useSpaceExperience();
  const reduceMotion = systemReduced || Boolean(space?.paused);
  const [finePointer, setFinePointer] = useState(false);
  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState('');
  const [active, setActive] = useState(false);
  const pointerX = useMotionValue(-100);
  const pointerY = useMotionValue(-100);
  const x = useSpring(pointerX, { stiffness: 240, damping: 28, mass: .45 });
  const y = useSpring(pointerY, { stiffness: 240, damping: 28, mass: .45 });
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setFinePointer(query.matches);
    update(); query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!finePointer || reduceMotion) { setVisible(false); return; }
    const move = (event) => {
      if (event.pointerType === 'touch') { setVisible(false); return; }
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('input, textarea, select, dialog, .mobile-menu')) { setVisible(false); return; }
      pointerX.set(event.clientX); pointerY.set(event.clientY); setVisible(true);
      const interactive = target.closest('a, button');
      setActive(Boolean(interactive));
      setLabel(interactive?.getAttribute('data-pointer-label') || '');
    };
    const hide = () => setVisible(false);
    const onKey = (event) => { if (event.key === 'Tab') hide(); };
    window.addEventListener('pointermove', move, { passive: true });
    document.documentElement.addEventListener('pointerleave', hide);
    window.addEventListener('blur', hide);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', hide, { passive: true });
    return () => {
      window.removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('pointerleave', hide);
      window.removeEventListener('blur', hide);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', hide);
    };
  }, [finePointer, reduceMotion, pointerX, pointerY]);

  return <>
    <motion.div className="portfolio-reading-progress" style={{ scaleX: scrollYProgress }} aria-hidden="true" />
    {finePointer && !reduceMotion &&
      <motion.div className={`pointer-accent ${active ? 'pointer-active' : ''}`} style={{ x, y, opacity: visible ? 1 : 0 }} aria-hidden="true"><div className="pointer-reticle"><i /><i /><i /><i /></div>{label && <span className="pointer-label">{label} ↗</span>}</motion.div>
    }
  </>;
}
